// Who else can get into Superadmin. platform_admins has no email column of
// its own (that lives on auth.users) and no client-writable RLS policy at
// all -- granting/revoking platform-wide superadmin rights is sensitive
// enough that it only ever happens through SECURITY DEFINER functions
// (list_platform_admins, set_platform_admin_active) or the
// invite-superadmin Edge Function, never a plain table read/write. Inviting
// someone uses Supabase Auth's own inviteUserByEmail, so they set their own
// password -- nobody here ever sees or sets it for them.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { ShieldCheck, UserPlus, Mail } from 'lucide-react';
import { logPlatformAdminAction } from '@/lib/platformAuditLog';

interface PlatformAdminRow {
  user_id: string;
  email: string;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

const SuperadminUsers: React.FC = () => {
  const [selfUserId, setSelfUserId] = useState<string | null>(null);
  const [admins, setAdmins] = useState<PlatformAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNotes, setInviteNotes] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setSelfUserId(data.user?.id ?? null));
  }, []);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('list_platform_admins');
    if (error) {
      toast({ title: 'Error', description: 'Failed to load superadmins: ' + error.message, variant: 'destructive' });
      setLoading(false);
      return;
    }
    setAdmins((data as PlatformAdminRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (userId: string, nextActive: boolean) => {
    setTogglingId(userId);
    const { error } = await supabase.rpc('set_platform_admin_active', { p_user_id: userId, p_active: nextActive });
    setTogglingId(null);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return;
    }
    setAdmins((prev) => prev.map((a) => (a.user_id === userId ? { ...a, is_active: nextActive } : a)));
    const email = admins.find((a) => a.user_id === userId)?.email;
    logPlatformAdminAction('superadmin_active_changed', 'platform_admin', userId, { email, is_active: nextActive });
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const { data, error } = await supabase.functions.invoke('invite-superadmin', {
      body: { email: inviteEmail.trim(), notes: inviteNotes.trim() },
    });
    setInviting(false);
    if (error || data?.error) {
      toast({ title: 'Error', description: data?.error || error?.message || 'Failed to send invite', variant: 'destructive' });
      return;
    }
    setShowInvite(false);
    setInviteEmail('');
    setInviteNotes('');
    toast({ title: 'Invite sent', description: `${inviteEmail.trim()} can now set their own password from the email Supabase just sent them.` });
    logPlatformAdminAction('superadmin_invited', 'platform_admin', null, { email: inviteEmail.trim() });
    load();
  };

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base text-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-yellow-500" />
              Superadmins
            </CardTitle>
            <Button size="sm" className="golden-button" onClick={() => setShowInvite(true)}>
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              Invite
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Platform-wide access, not scoped to any tenant -- give this out sparingly.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : admins.length === 0 ? (
            <p className="text-sm text-muted-foreground">No superadmins found.</p>
          ) : (
            admins.map((a) => {
              const isSelf = a.user_id === selfUserId;
              return (
                <div
                  key={a.user_id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-secondary/20"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {a.email}
                        {isSelf && <span className="text-xs text-muted-foreground ml-1.5">(you)</span>}
                      </p>
                      {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
                    </div>
                  </div>
                  <Switch
                    checked={a.is_active}
                    onCheckedChange={(checked) => toggleActive(a.user_id, checked)}
                    disabled={togglingId === a.user_id || isSelf}
                    title={isSelf ? "You can't deactivate your own access" : undefined}
                  />
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Superadmin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="them@example.com"
              />
              <p className="text-xs text-muted-foreground">
                They'll get an email from Supabase to set their own password -- nothing to note down here.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-notes">Notes (optional)</Label>
              <Input id="invite-notes" value={inviteNotes} onChange={(e) => setInviteNotes(e.target.value)} placeholder="Why they have access" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>
              Cancel
            </Button>
            <Button onClick={sendInvite} disabled={inviting || !inviteEmail.trim()} className="golden-button">
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminUsers;
